import PropTypes from 'prop-types';
import React, { useState } from 'react';

import ContactForm from '$partials/forms/contact-form';
import { Footer, Header, Meta } from '$partials/global';
import RichText from '$partials/global/rich-text';
import ModuleRelatedCarousel from '$partials/modules/individual/related-carousel';
import CodeSnippet from '$partials/global/code-snippet';
import {
    IconArrowRight
} from '$partials/icons/index.js';
import Button from '$partials/ui/button.jsx';
import { GlobalLayoutProvider, GlobalLayoutContext } from '$contexts/global-layout';
import { useLocale } from '$contexts/locale.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const ContactPage = ({ data, domain }) => {
    const [stylingWhite, setStylingWhite] = useState(true);

    const page = data?.page;

    const {
        formatURI,
        translate: t,
    } = useLocale();

    return (
        <>
            <Meta page={page} />

            <main
                id='main-content'>
                <div className='min-h-screen'>

                    <GlobalLayoutProvider>
                        <GlobalLayoutContext.Consumer>
                            {() => (
                                <Header
                                    menu={data?.navigation}
                                    setStylingWhite={setStylingWhite}
                                    stylingWhite={stylingWhite} />
                            )}
                        </GlobalLayoutContext.Consumer>
                    </GlobalLayoutProvider>

                    <div className='layout-container'>

                        <article className='flex flex-col sm:flex-row md:justify-between mt-90 md:mt-160'>
                            {/* Header */}
                            <header className='w-full sm:w-6/12 md:w-5/12 px-10 sm:px-5 md:px-10 mb-40'>
                                <h1 className='type-style-2 inline mr-10'>{page.title}</h1>

                                {page?.copy &&
                                    <div className='mt-20 md:mt-30'>
                                        <RichText
                                            className='rich-text'
                                            {...page.copy} />
                                    </div>
                                }
                                {page?.additionalCopy &&
                                    <div className='hidden sm:block mt-20 md:mt-30'>
                                        <RichText
                                            className='rich-text'
                                            {...page.additionalCopy} />
                                    </div>
                                }

                                {page?.subContactCtaLabel && page?.subContactCtaLink &&
                                    <div className='hidden sm:block'>
                                        <Button
                                            as='a'
                                            className='flex button button--tertiary mt-20 items-center'
                                            href={formatURI(page?.subContactCtaLink)}>
                                            <span>
                                                {page?.subContactCtaLabel}
                                            </span>
                                            <span className='ml-5'>
                                                <IconArrowRight />
                                            </span>
                                        </Button>
                                    </div>
                                }
                            </header>

                            {/* Form */}
                            <section className='w-full sm:w-6/12 md:w-6/12 mb-40'>
                                {page.embedCode ? (
                                    <CodeSnippet html={page.embedCode} />
                                ) : (
                                    <ContactForm
                                        formConsentOptions={data.formConsentOptions}
                                        formEmail={page?.formEmailAddress}
                                        formFields={data.formFields}
                                        formId={data.formId}
                                        pageType={'bvaBusinessContactPageCollection'}
                                        slug={page.slug}
                                        title={page.title} />
                                )}
                            </section>

                            {/* Mobile */}
                            <div className='px-10 sm:hidden'>
                                {page?.additionalCopy &&
                                    <div className='mt-20 md:mt-30'>
                                        <RichText
                                            className='rich-text'
                                            {...page.additionalCopy} />
                                    </div>
                                }
                                {page?.subContactCtaLabel && page?.subContactCtaLink &&
                                    <Button
                                        as='a'
                                        className='button button--tertiary mt-20 flex items-center'
                                        href={formatURI(page?.subContactCtaLink)}>
                                        <span>
                                            {page?.subContactCtaLabel}
                                        </span>
                                        <span className='ml-5'>
                                            <IconArrowRight />
                                        </span>
                                    </Button>
                                }
                            </div>
                        </article>
                    </div>
                    {data?.relatedArticles?.items?.length > 0 && (
                        <div className='mt-80 md:mt-115'>
                            <ModuleRelatedCarousel
                                items={data?.relatedArticles?.items}
                                title={data?.page?.relatedArticlesHeader} />
                        </div>
                    )}
                    <div className='pt-40 md:pt-60'>
                        <Footer
                            domain={domain}
                            menu={data?.footer} />
                    </div>
                </div>
            </main>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    data: PropTypes.shape({
        page: PropTypes.object,
        footer: PropTypes.shape({
            items: PropTypes.array
        }),
        formFields: PropTypes.array,
        formId: PropTypes.string,
        formConsentOptions: PropTypes.object,
        navigation: PropTypes.object,
        showHideNewsletter: PropTypes.bool,
        relatedArticles: PropTypes.object,
    }),
    domain: PropTypes.string,
};

ContactPage.propTypes = propTypes;

export default ContactPage;
